import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Plus, Copy, Check, CheckCircle, WarningTriangle, Xmark, Key, OpenBook, ShieldXmark, Trash, InfoCircle } from 'iconoir-react';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from './ConfirmModal';

interface ApiToken {
  id: number;
  name: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  is_expired: boolean;
}

interface Scope {
  scope: string;
  description: string;
}

interface Feedback {
  type: 'success' | 'error';
  message: string;
}

const MAX_VISIBLE_SCOPES = 3;

const CATEGORY_LABELS: Record<string, string> = {
  boards: 'Tableaux',
  cards: 'Cartes',
  columns: 'Colonnes',
  labels: 'Étiquettes',
  attachments: 'Pièces jointes',
  comments: 'Commentaires',
  members: 'Membres',
};

const ApiTokensManagement: React.FC = () => {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [availableScopes, setAvailableScopes] = useState<Scope[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    scopes: [] as string[],
    expires_at: '',
  });
  const [tokenToDelete, setTokenToDelete] = useState<number | null>(null);
  const [showRevokeAllConfirm, setShowRevokeAllConfirm] = useState(false);
  const [expandedTokenId, setExpandedTokenId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const showFeedback = (type: Feedback['type'], message: string) => {
    setFeedback({ type, message });
    if (type === 'success') {
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [tokensRes, scopesRes] = await Promise.all([
        api.get('/api-tokens'),
        api.get('/api-tokens/scopes'),
      ]);
      setTokens(tokensRes.data);
      setAvailableScopes(scopesRes.data.scopes);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/api-tokens', formData);
      setNewToken(response.data.token);
      setShowCreateForm(false);
      setFormData({ name: '', scopes: [], expires_at: '' });
      await loadData();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      showFeedback('error', 'Erreur lors de la création du token.');
    }
  };

  const handleDeleteToken = (tokenId: number) => {
    setTokenToDelete(tokenId);
  };

  const handleConfirmDeleteToken = async () => {
    const tokenId = tokenToDelete;
    setTokenToDelete(null);
    if (tokenId === null) return;

    try {
      await api.delete(`/api-tokens/${tokenId}`);
      await loadData();
      showFeedback('success', 'Token supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showFeedback('error', 'Erreur lors de la suppression du token.');
    }
  };

  const handleRevokeAll = () => {
    setShowRevokeAllConfirm(true);
  };

  const handleConfirmRevokeAll = async () => {
    setShowRevokeAllConfirm(false);

    try {
      await api.post('/api-tokens/revoke-all');
      await loadData();
      showFeedback('success', 'Tous les tokens ont été révoqués');
    } catch (error) {
      console.error('Erreur lors de la révocation:', error);
      showFeedback('error', 'Erreur lors de la révocation des tokens.');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const toggleScope = (scope: string) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const getScopeCategory = (scope: string) => {
    return scope.split(':')[0];
  };

  const groupedScopes = availableScopes.reduce((acc, scope) => {
    const category = getScopeCategory(scope.scope);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(scope);
    return acc;
  }, {} as Record<string, Scope[]>);

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="api-tokens-management">
      <div className="api-section-header">
        <div className="api-section-heading">
          <div className="api-section-icon">
            <Key width={22} height={22} strokeWidth={2} />
          </div>
          <div>
            <h2>Tokens API</h2>
            <p className="api-section-subtitle">
              Les tokens API permettent de connecter Ventilio à des applications et services externes.
            </p>
          </div>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/api-documentation')}
        >
          <OpenBook width={18} height={18} strokeWidth={2} />
          Documentation API
        </button>
      </div>

      {feedback && (
        <div
          className={feedback.type === 'success' ? 'settings-success' : 'settings-error'}
          role={feedback.type === 'success' ? 'status' : 'alert'}
        >
          {feedback.type === 'success' ? (
            <Check width={18} height={18} strokeWidth={2} />
          ) : (
            <WarningTriangle width={18} height={18} strokeWidth={2} />
          )}
          <span>{feedback.message}</span>
          <button
            type="button"
            className="api-feedback-close"
            onClick={() => setFeedback(null)}
            aria-label="Fermer le message"
          >
            <Xmark width={14} height={14} strokeWidth={2} />
          </button>
        </div>
      )}
      {newToken && (
        <div className="api-token-success">
          <div className="api-token-success-header">
            <CheckCircle width={20} height={20} strokeWidth={2} />
            <h3>Token créé avec succès</h3>
          </div>
          <p>
            Copiez ce token maintenant et conservez-le en lieu sûr. Il ne sera plus affiché
            après avoir quitté cet écran.
          </p>
          <div className="api-token-display">
            <code>{newToken}</code>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => copyToClipboard(newToken)}
            >
              {copiedToken ? (
                <>
                  <Check width={16} height={16} strokeWidth={2} />
                  Copié !
                </>
              ) : (
                <>
                  <Copy width={16} height={16} strokeWidth={2} />
                  Copier
                </>
              )}
            </button>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setNewToken(null)}
          >
            J'ai copié le token
          </button>
        </div>
      )}

      <div className="api-actions-bar">
        <div className="api-actions-bar-left">
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            <Plus width={18} height={18} strokeWidth={2} />
            Nouveau token
          </button>
          {tokens.length > 0 && (
            <span className="api-token-count">
              {tokens.length} token{tokens.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {tokens.length > 0 && (
          <button className="btn btn-sm btn-danger" onClick={handleRevokeAll}>
            <ShieldXmark width={16} height={16} strokeWidth={2} />
            Révoquer tous les tokens
          </button>
        )}
      </div>

      {/* Formulaire de création */}
      {showCreateForm && (
        <div className="api-token-form-modal">
          <div className="api-token-form-content">
            <h3>Créer un nouveau token API</h3>
            <form onSubmit={handleCreateToken}>
              <div className="form-group">
                <label>Nom du token</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Zapier Integration"
                  className="form-control"
                  required
                />
                <small>Donnez un nom descriptif pour identifier ce token</small>
              </div>

              <div className="form-group">
                <label>Permissions (Scopes)</label>
                <div className="api-scopes-grid">
                  {Object.entries(groupedScopes).map(([category, scopes]) => (
                    <div key={category} className="api-scope-category">
                      <h4>{CATEGORY_LABELS[category] ?? category}</h4>
                      {scopes.map((scope) => (
                        <label key={scope.scope} className="api-scope-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.scopes.includes(scope.scope)}
                            onChange={() => toggleScope(scope.scope)}
                          />
                          <span>{scope.description}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Date d'expiration (optionnel)</label>
                <input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="form-control"
                  min={new Date().toISOString().split('T')[0]}
                />
                <small>Laissez vide pour un token sans expiration</small>
              </div>

              <div className="api-token-form-footer">
                <button type="submit" className="btn btn-primary" disabled={formData.scopes.length === 0}>
                  Créer le token
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateForm(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Liste des tokens */}
      {tokens.length === 0 ? (
        <div className="api-empty-state">
          <div className="api-empty-state-icon">
            <Key width={26} height={26} strokeWidth={1.5} />
          </div>
          <h3>Aucun token API</h3>
          <p>Créez un token pour connecter Ventilio à un service externe.</p>
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            <Plus width={18} height={18} strokeWidth={2} />
            Créer un token
          </button>
        </div>
      ) : (
        <div className="api-tokens-card">
          <div className="api-tokens-table-wrapper">
            <table className="api-tokens-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Permissions</th>
                  <th>Dernière utilisation</th>
                  <th>Expiration</th>
                  <th>Créé le</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id} className={token.is_expired ? 'api-token-expired' : ''}>
                    <td>
                      <strong>{token.name}</strong>
                      {token.is_expired && <span className="api-badge-expired">Expiré</span>}
                    </td>
                    <td>
                      <div className="api-token-scopes">
                        {token.scopes.slice(0, MAX_VISIBLE_SCOPES).map((scope) => (
                          <span key={scope} className="api-scope-badge">
                            {scope}
                          </span>
                        ))}
                        {token.scopes.length > MAX_VISIBLE_SCOPES && (
                          <button
                            type="button"
                            className="api-scope-badge api-scope-badge-more"
                            onClick={() => setExpandedTokenId(
                              expandedTokenId === token.id ? null : token.id
                            )}
                            aria-expanded={expandedTokenId === token.id}
                            aria-controls={`api-token-scopes-extra-${token.id}`}
                            aria-label={
                              expandedTokenId === token.id
                                ? `Masquer les ${token.scopes.length - MAX_VISIBLE_SCOPES} permissions supplémentaires`
                                : `Afficher les ${token.scopes.length - MAX_VISIBLE_SCOPES} permissions supplémentaires`
                            }
                          >
                            {expandedTokenId === token.id
                              ? 'Réduire'
                              : `+${token.scopes.length - MAX_VISIBLE_SCOPES}`}
                          </button>
                        )}
                      </div>
                      {expandedTokenId === token.id && (
                        <div className="api-token-scopes-extra" id={`api-token-scopes-extra-${token.id}`}>
                          {token.scopes.slice(MAX_VISIBLE_SCOPES).map((scope) => (
                            <span key={scope} className="api-scope-badge">
                              {scope}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {token.last_used_at
                        ? new Date(token.last_used_at).toLocaleDateString('fr-FR')
                        : 'Jamais'}
                    </td>
                    <td>
                      {token.expires_at
                        ? new Date(token.expires_at).toLocaleDateString('fr-FR')
                        : 'Jamais'}
                    </td>
                    <td>{new Date(token.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => handleDeleteToken(token.id)}
                        aria-label={`Supprimer le token ${token.name}`}
                        title="Supprimer le token"
                      >
                        <Trash width={16} height={16} strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="api-info-card">
        <InfoCircle width={18} height={18} strokeWidth={2} />
        <div>
          <strong>Bon à savoir</strong>
          <p>
            Conservez vos tokens en lieu sûr. Ils ne seront plus affichés après leur création.
            Vous pouvez révoquer un token à tout moment s'il n'est plus utilisé.
          </p>
        </div>
      </div>

      <ConfirmModal
        isOpen={tokenToDelete !== null}
        title="Supprimer le token"
        message="Êtes-vous sûr de vouloir supprimer ce token ? Cette action est irréversible."
        confirmText="Supprimer"
        onConfirm={handleConfirmDeleteToken}
        onCancel={() => setTokenToDelete(null)}
        variant="danger"
      />

      <ConfirmModal
        isOpen={showRevokeAllConfirm}
        title="Révoquer tous les tokens"
        message="Êtes-vous sûr de vouloir révoquer TOUS vos tokens ? Cette action est irréversible."
        confirmText="Révoquer tout"
        onConfirm={handleConfirmRevokeAll}
        onCancel={() => setShowRevokeAllConfirm(false)}
        variant="danger"
      />
    </div>
  );
};

export default ApiTokensManagement;
